/**
 * Task Detail Modal Component
 *
 * Displays full task details with ability to:
 * - View all task information
 * - Update task status (with required completion context)
 * - Reassign task to different user
 * - Edit task fields
 */

import React, { useState, useEffect } from 'react';
import {
  taskAPI,
  userAPI,
  alertAPI,
  TaskDetail,
  TaskCategory,
  TaskSeverity,
  TaskStatus,
  TaskUpdate,
  TaskStatusUpdate,
  TaskHistoryEntry,
  AlertDetail,
} from '../services/api';

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: number | null;
  categories: TaskCategory[];
  onTaskUpdated: () => void;
}

const SEVERITY_OPTIONS: { value: TaskSeverity; label: string; color: string }[] = [
  { value: 'high', label: 'High', color: 'text-red-600' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'low', label: 'Low', color: 'text-green-600' },
];

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  onClose,
  taskId,
  categories,
  onTaskUpdated,
}) => {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showAlertInfo, setShowAlertInfo] = useState(false);
  const [alertInfo, setAlertInfo] = useState<AlertDetail | null>(null);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editInstructions, setEditInstructions] = useState('');
  const [editSeverity, setEditSeverity] = useState<TaskSeverity>('medium');
  const [editDueDate, setEditDueDate] = useState('');
  const [editCategoryId, setEditCategoryId] = useState<number>(0);
  const [editAssignedToUserId, setEditAssignedToUserId] = useState<number | null>(null);

  // Status update state
  const [newStatus, setNewStatus] = useState<TaskStatus>('not_started');
  const [completionContext, setCompletionContext] = useState('');

  // Reference data
  const [users, setUsers] = useState<{ id: number; full_name: string; is_active: boolean }[]>([]);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTask();
      fetchUsers();
      setShowHistory(false);
      setHistory([]);
    }
  }, [isOpen, taskId]);

  const fetchHistory = async () => {
    if (!taskId) return;
    try {
      setHistoryLoading(true);
      const data = await taskAPI.getWithHistory(taskId);
      setHistory(data.history);
    } catch (err: any) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && history.length === 0) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  const getChangeTypeLabel = (changeType: string): string => {
    const labels: Record<string, string> = {
      created: 'Task Created',
      status_change: 'Status Changed',
      reassigned: 'Reassigned',
      completed: 'Completed',
      field_update: 'Updated',
    };
    return labels[changeType] || changeType;
  };

  const getChangeTypeColor = (changeType: string): string => {
    const colors: Record<string, string> = {
      created: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      status_change: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      reassigned: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      field_update: 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-300',
    };
    return colors[changeType] || 'bg-gray-100 text-gray-800';
  };

  const fetchTask = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await taskAPI.get(taskId);
      setTask(data);
      initializeEditForm(data);

      // Fetch linked alert info if exists
      if (data.alert_id) {
        try {
          const alert = await alertAPI.get(data.alert_id);
          setAlertInfo(alert);
        } catch (err) {
          console.error('Failed to fetch alert info:', err);
        }
      } else {
        setAlertInfo(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userAPI.list();
      setUsers(data.filter(u => u.is_active));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const initializeEditForm = (data: TaskDetail) => {
    setEditTitle(data.title);
    setEditInstructions(data.instructions || '');
    setEditSeverity(data.severity);
    setEditDueDate(data.due_date || '');
    setEditCategoryId(data.category_id);
    setEditAssignedToUserId(data.assigned_to_user_id);
    setNewStatus(data.status);
  };

  const handleSaveEdit = async () => {
    if (!task) return;
    try {
      setSaving(true);
      setError(null);

      const updateData: TaskUpdate = {
        title: editTitle,
        instructions: editInstructions || undefined,
        severity: editSeverity,
        due_date: editDueDate || undefined,
        category_id: editCategoryId,
        assigned_to_user_id: editAssignedToUserId || undefined,
      };

      await taskAPI.update(task.id, updateData);
      await fetchTask();
      setIsEditing(false);
      onTaskUpdated();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!task) return;

    // Validate completion context when completing
    if (newStatus === 'completed' && completionContext.trim().length < 10) {
      setError('Completion context is required (minimum 10 characters)');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const statusData: TaskStatusUpdate = {
        status: newStatus,
        completion_context: newStatus === 'completed' ? completionContext : undefined,
      };

      await taskAPI.updateStatus(task.id, statusData);
      await fetchTask();
      setShowStatusModal(false);
      setCompletionContext('');
      onTaskUpdated();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getSeverityBadge = (severity: TaskSeverity) => {
    const colors: Record<TaskSeverity, string> = {
      high: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
      medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
      low: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    };
    const labels: Record<TaskSeverity, string> = { high: 'High', medium: 'Medium', low: 'Low' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-bold ${colors[severity]}`}>
        {labels[severity]}
      </span>
    );
  };

  const getStatusBadge = (status: TaskStatus) => {
    const colors: Record<TaskStatus, string> = {
      not_started: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
      completed: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
      reassigned: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
    };
    const labels: Record<TaskStatus, string> = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      reassigned: 'Reassigned',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-bold ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: 'var(--bg-secondary)' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Task Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error && !task ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : task ? (
            <div className="space-y-4">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {isEditing ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Instructions
                    </label>
                    <textarea
                      value={editInstructions}
                      onChange={(e) => setEditInstructions(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Severity
                      </label>
                      <select
                        value={editSeverity}
                        onChange={(e) => setEditSeverity(e.target.value as TaskSeverity)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      >
                        {SEVERITY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={editDueDate}
                        onChange={(e) => setEditDueDate(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Category
                    </label>
                    <select
                      value={editCategoryId}
                      onChange={(e) => setEditCategoryId(Number(e.target.value))}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Assigned To
                    </label>
                    <select
                      value={editAssignedToUserId || ''}
                      onChange={(e) => setEditAssignedToUserId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border rounded-md"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>{user.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-4">
                  {/* Title and Status Row */}
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                      {task.title}
                    </h3>
                    <div className="flex gap-2">
                      {getSeverityBadge(task.severity)}
                      {getStatusBadge(task.status)}
                    </div>
                  </div>

                  {/* Alert Link - Expandable */}
                  {task.alert_id && (
                    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                      <button
                        onClick={() => setShowAlertInfo(!showAlertInfo)}
                        className="w-full px-3 py-2 flex items-center justify-between text-sm bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      >
                        <span>
                          <span style={{ color: 'var(--text-secondary)' }}>Linked Alert: </span>
                          <span className="font-medium text-blue-600 dark:text-blue-400">#{task.alert_id}</span>
                        </span>
                        <svg
                          className={`w-4 h-4 transition-transform text-blue-600 dark:text-blue-400 ${showAlertInfo ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showAlertInfo && alertInfo && (
                        <div className="px-3 py-2 border-t text-sm space-y-2" style={{ borderColor: 'var(--border-color)' }}>
                          {alertInfo.description && (
                            <div>
                              <span className="text-xs uppercase" style={{ color: 'var(--text-secondary)' }}>Description</span>
                              <div style={{ color: 'var(--text-primary)' }}>{alertInfo.description}</div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-xs uppercase" style={{ color: 'var(--text-secondary)' }}>KPI</span>
                              <div style={{ color: 'var(--text-primary)' }}>
                                {alertInfo.kpi_name}: {alertInfo.kpi_value ?? '-'}
                              </div>
                            </div>
                            <div>
                              <span className="text-xs uppercase" style={{ color: 'var(--text-secondary)' }}>Location</span>
                              <div style={{ color: 'var(--text-primary)' }}>
                                {alertInfo.department_name || '-'}
                                {alertInfo.machine_name && ` / ${alertInfo.machine_name}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Instructions */}
                  {task.instructions && (
                    <div>
                      <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                        Instructions
                      </div>
                      <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                        {task.instructions}
                      </div>
                    </div>
                  )}

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Category</div>
                      <div style={{ color: 'var(--text-primary)' }}>{task.category_name}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Assigned To</div>
                      <div style={{ color: 'var(--text-primary)' }}>{task.assigned_to_name || 'Unassigned'}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Due Date</div>
                      <div style={{ color: 'var(--text-primary)' }}>{formatDate(task.due_date)}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Created By</div>
                      <div style={{ color: 'var(--text-primary)' }}>{task.created_by_name}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Created At</div>
                      <div style={{ color: 'var(--text-primary)' }}>{formatDateTime(task.created_at)}</div>
                    </div>
                  </div>

                  {/* Completion Context (if completed) */}
                  {task.status === 'completed' && task.completion_context && (
                    <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      <div className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
                        Completion Notes
                      </div>
                      <div className="text-sm text-green-800 dark:text-green-200">
                        {task.completion_context}
                      </div>
                      <div className="text-xs mt-2 text-green-600 dark:text-green-400">
                        Completed: {formatDateTime(task.completed_at)}
                      </div>
                    </div>
                  )}

                  {/* History Toggle Button */}
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <button
                      onClick={toggleHistory}
                      className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      View History ({historyLoading ? '...' : history.length || 'load'})
                    </button>

                    {/* History List */}
                    {showHistory && (
                      <div className="mt-3 space-y-2">
                        {historyLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          </div>
                        ) : history.length === 0 ? (
                          <div className="text-sm text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                            No history available
                          </div>
                        ) : (
                          <div className="relative">
                            {/* Timeline line */}
                            <div
                              className="absolute left-2 top-2 bottom-2 w-0.5"
                              style={{ background: 'var(--border-color)' }}
                            ></div>

                            {history.map((entry, idx) => (
                              <div key={entry.id} className="relative pl-6 pb-3">
                                {/* Timeline dot */}
                                <div
                                  className="absolute left-0 top-1.5 w-4 h-4 rounded-full border-2 bg-white dark:bg-gray-800"
                                  style={{ borderColor: 'var(--border-color)' }}
                                ></div>

                                <div
                                  className="p-2 rounded-lg text-sm"
                                  style={{ background: 'var(--bg-primary)' }}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getChangeTypeColor(entry.change_type)}`}>
                                      {getChangeTypeLabel(entry.change_type)}
                                    </span>
                                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                      {formatDateTime(entry.created_at)}
                                    </span>
                                  </div>

                                  <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {entry.change_type === 'created' && (
                                      <span>Task created by <strong>{entry.changed_by_name}</strong></span>
                                    )}
                                    {entry.change_type === 'status_change' && (
                                      <span>
                                        Status changed from <strong>{entry.old_status}</strong> to{' '}
                                        <strong>{entry.new_status}</strong> by <strong>{entry.changed_by_name}</strong>
                                      </span>
                                    )}
                                    {entry.change_type === 'reassigned' && (
                                      <span>
                                        Reassigned from <strong>{entry.old_assigned_to_name || 'Unassigned'}</strong> to{' '}
                                        <strong>{entry.new_assigned_to_name}</strong> by <strong>{entry.changed_by_name}</strong>
                                      </span>
                                    )}
                                    {entry.change_type === 'completed' && (
                                      <span>Completed by <strong>{entry.changed_by_name}</strong></span>
                                    )}
                                  </div>

                                  {entry.notes && (
                                    <div
                                      className="mt-1 text-xs italic p-2 rounded"
                                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
                                    >
                                      "{entry.notes}"
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status Update Modal */}
              {showStatusModal && (
                <div className="mt-4 p-4 rounded-lg border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
                  <h4 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                    Update Status
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        New Status
                      </label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value as TaskStatus)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {newStatus === 'completed' && (
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                          Completion Notes <span className="text-red-500">*</span>
                          <span className="text-xs font-normal ml-1" style={{ color: 'var(--text-secondary)' }}>
                            (min 10 characters)
                          </span>
                        </label>
                        <textarea
                          value={completionContext}
                          onChange={(e) => setCompletionContext(e.target.value)}
                          rows={3}
                          placeholder="Describe what was done to complete this task..."
                          className="w-full px-3 py-2 border rounded-md"
                          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                        />
                        <div className="text-xs mt-1" style={{ color: completionContext.length >= 10 ? 'var(--text-secondary)' : 'rgb(239, 68, 68)' }}>
                          {completionContext.length}/10 characters minimum
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={handleStatusUpdate}
                        disabled={saving || (newStatus === 'completed' && completionContext.trim().length < 10)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setShowStatusModal(false);
                          setCompletionContext('');
                          setNewStatus(task.status);
                        }}
                        className="px-4 py-2 text-sm font-medium rounded-md"
                        style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer Actions */}
        {task && !loading && (
          <div className="flex justify-between items-center p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex gap-2">
              {!isEditing && !showStatusModal && task.status !== 'completed' && (
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Update Status
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      initializeEditForm(task);
                    }}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md"
                    style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || !editTitle.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={task.status === 'completed'}
                    className="px-4 py-2 text-sm font-medium rounded-md border disabled:opacity-50"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailModal;
